import {
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@design-system';
import { PageLink } from '@/components';
import CompanyLogoContainer from '@/components/CompanyLogo';
import { ClientUserMenu } from '@/components/ClientUserMenu';
import { ClientDataModal } from '@/components/ClientDataModal';
import { ContactAdvisorButton } from '@/components/ContactAdvisorButton';
import { RequestAdvisorMeetingModal } from '@/components/RequestAdvisorMeetingModal';
import { VideoLightbox } from '@/components/VideoLightbox';

import { Check, CirclePlay, Minus, LoaderCircle, Menu, XIcon } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useMemo, useState, type FunctionComponent } from 'react';
import {
  useSlideConfig,
  useSlideHistory,
  useOnboardingNavigation,
  useOnboardingHeader as useOnboardingHeaderHook,
  useIsMobileMenuOpen,
  useMobileMenuActions,
  getStepDefs,
  isBaseFilteredFromFullList,
  isD2CPaywallLocked,
  ONBOARDING_SLIDES,
  SLIDE_VIDEO_URLS,
} from '@/features/client-onboarding';

import { selectClient, selectFeatureFlags, selectSession } from '@/model';
import { SessionType } from '@shared';
import { EdjPilotRestricted } from '@/shared/components/edj-pilot-restricted';

interface StepProgress {
  name: string;
  beginningSlideIndex: number;
  lastSlideIndex: number;
  isOptional: boolean;
}

export function OnboardingHeader() {
  const header = useOnboardingHeaderHook();
  const client = useSelector(selectClient)!;
  const slideHistory = useSlideHistory();
  const { hideProgressStepper, slidePage } = useSlideConfig();
  const isMobileMenuOpen = useIsMobileMenuOpen();
  const { toggleMobileMenu } = useMobileMenuActions();
  const [showVideoModal, setShowVideoModal] = useState(false);

  const videoUrl = slidePage ? SLIDE_VIDEO_URLS.get(slidePage) : undefined;

  return (
    <>
      <div className="bg-background border-b p-4 md:px-6">
        <div className="flex flex-col lg:grid lg:grid-cols-[1fr_minmax(max-content,64rem)_1fr] lg:items-center lg:gap-6">
          {header.isClient ? (
            <div className="mb-3 flex items-center justify-between lg:contents">
              <CompanyLogoContainer
                organizationDisplayName={header.advisor.organizationDisplayName}
              />
              <div className="ml-auto flex items-center gap-2 lg:order-3 lg:justify-self-end">
                <ContactAdvisorButton
                  className="hidden sm:flex"
                  requestMeetingOnClick={header.openRequestMeeting}
                />
                {header.showIntakeFormButton && (
                  <div className="hidden sm:block">
                    <EdjPilotRestricted>
                      <IntakeFormButton
                        tooltip={header.intakeFormTooltip}
                        onClick={header.onIntakeFormClick}
                        disabled={header.isClient && header.isLoadingForm}
                        isLoading={header.isLoadingForm}
                        buttonText={header.buttonText}
                      />
                    </EdjPilotRestricted>
                  </div>
                )}
                {videoUrl && (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="size-9 rounded-full md:size-10 xl:hidden"
                    onClick={() => setShowVideoModal(true)}
                    aria-label="Explain slide"
                  >
                    <CirclePlay />
                  </Button>
                )}
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className={cn(
                    'size-9 rounded-full md:size-10 xl:hidden',
                    isMobileMenuOpen && 'hidden',
                  )}
                  onClick={toggleMobileMenu}
                  aria-label="Toggle navigation menu"
                  data-testid="onboarding-menu-button"
                >
                  <Menu />
                </Button>
                <ClientUserMenu
                  handleClickSignOut={header.handleSignOut}
                  clientEmail={header.clientEmail}
                  onNavigateHome={header.navigateToClientHome}
                  showEmail={false}
                />
              </div>
            </div>
          ) : (
            <div className="mb-3 flex items-center justify-between lg:contents">
              <CompanyLogoContainer
                organizationDisplayName={header.advisor.organizationDisplayName}
                onClick={header.navigateToClientsDashboard}
              />
              <div className="ml-auto flex items-center gap-2 lg:order-3 lg:justify-self-end">
                {header.showIntakeFormButton && (
                  <div className="hidden sm:block">
                    <EdjPilotRestricted>
                      <IntakeFormButton
                        tooltip={header.intakeFormTooltip}
                        onClick={header.onIntakeFormClick}
                        disabled={false}
                        isLoading={header.isLoadingForm}
                        buttonText={header.buttonText}
                      />
                    </EdjPilotRestricted>
                  </div>
                )}
                {videoUrl && (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="size-9 rounded-full md:size-10 xl:hidden"
                    onClick={() => setShowVideoModal(true)}
                    aria-label="Explain slide"
                  >
                    <CirclePlay />
                  </Button>
                )}
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className={cn(
                    'size-9 rounded-full md:size-10 xl:hidden',
                    isMobileMenuOpen && 'hidden',
                  )}
                  onClick={toggleMobileMenu}
                  aria-label="Toggle navigation menu"
                  data-testid="onboarding-menu-button"
                >
                  <Menu />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="size-9 rounded-full md:size-10"
                  onClick={header.navigateToClientsDashboard}
                  aria-label="Close and return to dashboard"
                >
                  <XIcon />
                </Button>
              </div>
            </div>
          )}
          {slidePage && (
            <div
              className={cn('lg:order-2', {
                'invisible h-0': hideProgressStepper,
              })}
            >
              <ProgressStepper
                client={client}
                slideHistory={slideHistory}
                slidePage={slidePage}
              />
            </div>
          )}
        </div>
      </div>

      <RequestAdvisorMeetingModal
        open={header.isRequestMeetingOpen}
        onClose={header.closeRequestMeeting}
      />
      {!header.isClient && header.client && (
        <ClientDataModal
          partialClient={header.client}
          open={header.isQuickEditOpen}
          initialMode="edit"
          onClose={header.closeQuickEdit}
        />
      )}
      {videoUrl && (
        <VideoLightbox
          videoUrl={videoUrl}
          isOpen={showVideoModal}
          onOpenChange={setShowVideoModal}
        />
      )}
    </>
  );
}

function IntakeFormButton({
  tooltip,
  onClick,
  disabled,
  isLoading,
  buttonText,
}: {
  tooltip: string;
  onClick: () => void;
  disabled: boolean;
  isLoading: boolean;
  buttonText: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Button
            onClick={onClick}
            disabled={disabled}
            variant="outline"
            className="h-9 rounded-full md:h-10"
            data-testid="update-intake-form-button"
          >
            {isLoading ? 'Loading...' : buttonText}
          </Button>
        </TooltipTrigger>
        <TooltipContent
          align="end"
          side="bottom"
          className="max-w-xs"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ProgressStepper({
  client,
  slideHistory,
  slidePage,
}: {
  client: Client;
  slideHistory: number[];
  slidePage: FunctionComponent<ClientIdProps>;
}) {
  const featureFlags = useSelector(selectFeatureFlags);
  const { sessionType, clientStripe } = useSelector(selectSession);
  const navigation = useOnboardingNavigation({ slidePage });
  const { globalSlideIndex, relativeSlideIndex, filteredSlides } = navigation;

  const isVbShopFiltered = isBaseFilteredFromFullList(client, featureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const progressPercentage = (relativeSlideIndex / filteredSlides.length) * 100;

  const steps: StepProgress[] = useMemo(() => {
    const stepDefs = getStepDefs(client, isD2C);
    return stepDefs.map(step => ({
      ...step,
      isOptional: false,
    }));
  }, [client, isD2C]);

  const currentStep = useMemo(
    () =>
      steps.find(
        step =>
          globalSlideIndex >= step.beginningSlideIndex && globalSlideIndex <= step.lastSlideIndex,
      ),
    [steps, globalSlideIndex],
  );

  const hasVisitedStep = (step: StepProgress): boolean => {
    return slideHistory.some(
      visitedIndex =>
        visitedIndex >= step.beginningSlideIndex && visitedIndex <= step.lastSlideIndex,
    );
  };

  if (isVbShopFiltered) {
    return (
      <div>
        <div className="bg-accent h-1 w-full rounded-full">
          <div
            className="bg-primary h-1 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <TooltipProvider delayDuration={0}>
        <nav
          aria-label="Progress"
          className="w-full"
        >
          <ol className="flex space-x-2 md:space-x-8">
            {steps.map(step => {
              const isCurrentStep = step === currentStep;
              const isPastStep = globalSlideIndex > step.lastSlideIndex;
              const wasVisited = hasVisitedStep(step);
              const isCompleted = isPastStep && wasVisited && !isCurrentStep;
              const isSkipped = isPastStep && !wasVisited && step.isOptional;
              const stepPercentComplete = calcStepPercentComplete(globalSlideIndex, step);

              const isLockedByPaywall = isD2CPaywallLocked(
                ONBOARDING_SLIDES[step.beginningSlideIndex],
                filteredSlides,
                isD2C,
                clientStripe,
              );

              return (
                <li
                  key={step.name}
                  className="flex-1"
                >
                  <PageLink
                    to={ONBOARDING_SLIDES[step.beginningSlideIndex]}
                    targetProps={{ clientId: client.clientId }}
                    className={cn('group flex flex-col', isLockedByPaywall && 'pointer-events-none opacity-50')}
                    onClick={e => {
                      if (isLockedByPaywall) e.preventDefault();
                    }}
                  >
                    <div
                      className={cn('mb-2 h-1 w-full rounded-full transition-colors duration-300', {
                        'bg-primary/30': isCurrentStep,
                        'bg-accent': !isCurrentStep && !isCompleted && !isSkipped,
                        'bg-primary': isCompleted,
                        'bg-muted': isSkipped,
                      })}
                    >
                      {(isCurrentStep || (isCompleted && stepPercentComplete < 100)) && (
                        <div
                          className={cn('h-1 rounded-full', {
                            'bg-primary': !isSkipped,
                            'bg-muted': isSkipped,
                          })}
                          style={{
                            width: `${isCompleted || isSkipped ? 100 : stepPercentComplete}%`,
                          }}
                        />
                      )}
                    </div>

                    <div
                      className={cn(
                        'text-foreground flex items-center gap-2 text-xs font-medium md:text-sm',
                        { 'text-primary font-semibold': isCurrentStep },
                      )}
                    >
                      {isCompleted && (
                        <div className="grid size-5 place-items-center rounded-full border-green-600/20 bg-green-600/5 text-green-800">
                          <Check className="size-4" />
                        </div>
                      )}
                      {isSkipped && (
                        <div className="bg-accent grid size-5 place-items-center rounded-full">
                          <Minus className="size-4" />
                        </div>
                      )}
                      {isCurrentStep && (
                        <LoaderCircle className="text-primary hidden size-4 self-center md:flex" />
                      )}
                      <span>{step.name}</span>
                      {step.isOptional && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge
                              variant="secondary"
                              className="hidden md:inline-flex"
                            >
                              Optional
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="max-w-xs"
                          >
                            These estimates are based on default assumptions. You can optionally
                            customize them to better reflect {client.clientFirstName}&apos;s
                            specific situation.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </PageLink>
                </li>
              );
            })}
          </ol>
        </nav>
      </TooltipProvider>
    </div>
  );
}

function calcStepPercentComplete(
  slideIndex: number,
  step: { beginningSlideIndex: number; lastSlideIndex: number },
): number {
  if (slideIndex < step.beginningSlideIndex) return 0;
  if (slideIndex > step.lastSlideIndex) return 100;

  return Math.round(
    ((slideIndex - step.beginningSlideIndex) /
      (step.lastSlideIndex - step.beginningSlideIndex + 1)) *
      100,
  );
}
